/**
 * Web replacement for the app's react-native-image-picker wrapper.
 *
 * On Android the app had to migrate to the system photo picker for Play policy
 * reasons; on the web the browser's own file dialog is already exactly that —
 * a scoped, one-shot picker that grants access to the chosen file only. No
 * permission prompt is involved.
 */

const MAX_DIM = 1200;
const QUALITY = 0.7;

// Downscale + re-encode so we don't POST a 12MP phone photo as base64.
function compress(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read that file'));
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      // SVG/HEIC and anything the canvas can't decode: pass through untouched.
      if (!/^data:image\/(png|jpe?g|webp)/i.test(dataUrl)) return resolve(dataUrl);

      const img = new Image();
      img.onerror = () => resolve(dataUrl);
      img.onload = () => {
        const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
        if (scale === 1 && dataUrl.length < 900_000) return resolve(dataUrl);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        try {
          resolve(canvas.toDataURL('image/jpeg', QUALITY));
        } catch (e) {
          resolve(dataUrl);
        }
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  });
}

// Opens a file dialog and resolves { uri, dataUri } — or null if cancelled.
function openPicker({ capture } = {}) {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    // `capture` asks a phone browser to open the camera directly.
    if (capture) input.setAttribute('capture', capture);
    input.style.position = 'fixed';
    input.style.left = '-9999px';
    document.body.appendChild(input);

    let settled = false;
    const done = (v) => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(v);
    };

    input.onchange = async () => {
      const file = input.files && input.files[0];
      if (!file) return done(null);
      try {
        const dataUri = await compress(file);
        done({ uri: dataUri, dataUri, name: file.name, size: file.size });
      } catch (e) {
        done(null);
      }
    };

    // There is no reliable "cancel" event; window focus returning without a
    // file means the dialog was dismissed.
    const onFocus = () => {
      window.removeEventListener('focus', onFocus);
      setTimeout(() => { if (!input.files || !input.files.length) done(null); }, 400);
    };
    setTimeout(() => window.addEventListener('focus', onFocus), 300);

    input.click();
  });
}

/** Pick an image from the device (gallery / files). */
export function pickImage() {
  return openPicker();
}

/** Capture a selfie — asks for the front camera on mobile, file dialog elsewhere. */
export function captureSelfie() {
  return openPicker({ capture: 'user' });
}

const imagePicker = { pickImage, captureSelfie };
export default imagePicker;
